import { useEffect, useState, useCallback } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  PageHeader,
  StatusBadge,
  TableSkeleton,
  EmptyState,
} from "@/components/shared";
import { money, fmtDate, label } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  Award,
  CheckCircle2,
  Clock,
  AlertTriangle,
  ShieldAlert,
  Plus,
  Lock,
} from "lucide-react";
import DirectPlacementDialog from "@/components/placements/DirectPlacementDialog";

const RISK_COLOR = { low: "green", medium: "amber", high: "red" };

export default function Placements() {
  const { can, user } = useAuth();
  const [data, setData] = useState(null);
  const [sel, setSel] = useState(null);
  const [directOpen, setDirectOpen] = useState(false);

  const canDirectPlace =
    user?.role === "bdm" ||
    user?.role === "super_admin" ||
    user?.role === "founder" ||
    user?.role === "administrator" ||
    can("*");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/placements", {
        params: { page_size: 40 },
      });
      setData(data);
    } catch (e) {
      toast.error(apiErr(e));
      setData({ items: [] });
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (id) => {
    try {
      const { data } = await api.get(`/placements/${id}`);
      setSel(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <div>
      <PageHeader
        title="Placements & Warranty"
        subtitle="Confirmed placements with Day 1/7/30/60/90/180 follow-ups and warranty tracking"
        actions={
          canDirectPlace ? (
            <Button
              onClick={() => setDirectOpen(true)}
              className="bg-[#255058] hover:bg-[#1c3d43] text-white"
              data-testid="direct-placement-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Direct Placement
            </Button>
          ) : null
        }
      />
      <DirectPlacementDialog
        open={directOpen}
        onOpenChange={setDirectOpen}
        onCreated={() => load()}
      />
      <Card className="overflow-hidden">
        {!data ? (
          <div className="p-4">
            <TableSkeleton />
          </div>
        ) : data.items.length === 0 ? (
          <EmptyState
            icon={Award}
            title="No placements yet"
            description="Placements are created when a candidate is hired."
          />
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Candidate</TableHead>
                  <TableHead className="hidden md:table-cell">Client</TableHead>
                  <TableHead>Fee</TableHead>
                  <TableHead className="hidden lg:table-cell">Start</TableHead>
                  <TableHead className="hidden lg:table-cell">
                    Guarantee Ends
                  </TableHead>
                  <TableHead>Warranty</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((p) => (
                  <TableRow
                    key={p.id}
                    data-testid={`placement-row-${p.id}`}
                    className="hover:bg-accent/40"
                  >
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-1.5">
                        {p.candidate_name}
                        {p.is_private && (
                          <span
                            title="Private / Restricted"
                            className="inline-flex items-center gap-1 rounded-full bg-[#c65a63]/10 text-[#c65a63] text-[10px] px-1.5 py-0.5"
                            data-testid={`placement-private-${p.id}`}
                          >
                            <Lock className="h-3 w-3" /> private
                          </span>
                        )}
                        {p.source === "direct_bdm_entry" && (
                          <span className="inline-flex items-center rounded-full bg-[#255058]/10 text-[#255058] text-[10px] px-1.5 py-0.5">
                            direct
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                      {p.client_name}
                    </TableCell>
                    <TableCell>
                      {p._masked ? (
                        <span className="text-xs text-muted-foreground">restricted</span>
                      ) : (
                        money(p.placement_fee, p.currency)
                      )}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {fmtDate(p.start_date)}
                    </TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">
                      {fmtDate(p.guarantee_end)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium status-${RISK_COLOR[p.warranty_risk] || "gray"}`}
                      >
                        {label(p.warranty_risk || "—")}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openDetail(p.id)}
                        data-testid={`placement-manage-${p.id}`}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
      <PlacementSheet
        placement={sel}
        onClose={() => setSel(null)}
        canManage={can("placement:manage")}
        onChange={(p) => {
          setSel(p);
          load();
        }}
      />
    </div>
  );
}

function PlacementSheet({ placement, onClose, canManage, onChange }) {
  const [risk, setRisk] = useState("");
  const [empStatus, setEmpStatus] = useState("");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (placement) {
      setRisk(placement.warranty_risk || "low");
      setEmpStatus(placement.employment_status || "active");
      setNote("");
    }
  }, [placement]);
  if (!placement) return null;

  const completeFollowup = async (day) => {
    try {
      const { data } = await api.put(`/placements/${placement.id}/followup`, {
        day,
        status: "completed",
        note: "Check-in completed",
      });
      toast.success(`Day ${day} check-in completed`);
      onChange(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };
  const saveWarranty = async () => {
    const replacement = empStatus === "resigned" || empStatus === "terminated";
    try {
      const { data } = await api.put(`/placements/${placement.id}/warranty`, {
        warranty_risk: risk,
        employment_status: empStatus,
        replacement_needed: replacement,
        resignation: empStatus === "resigned",
        termination: empStatus === "terminated",
        note,
      });
      toast.success(
        "Warranty updated" + (replacement ? " — replacement task created" : ""),
      );
      onChange(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  return (
    <Sheet open={!!placement} onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        className="w-full sm:max-w-xl overflow-y-auto scrollbar-thin"
        data-testid="placement-sheet"
      >
        <SheetHeader>
          <SheetTitle className="font-display">
            {placement.candidate_name}
          </SheetTitle>
          <SheetDescription>
            {placement.job_title} · {placement.client_name}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-6">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Info
              k="Placement Fee"
              v={money(placement.placement_fee, placement.currency)}
            />
            <Info k="Start Date" v={fmtDate(placement.start_date)} />
            <Info k="Guarantee Ends" v={fmtDate(placement.guarantee_end)} />
            <Info
              k="Employment"
              v={label(placement.employment_status || "active")}
            />
          </div>

          <div>
            <div className="font-display font-semibold mb-3">
              Follow-up Milestones
            </div>
            <div className="space-y-2">
              {(placement.follow_ups || []).map((f) => {
                const done = f.status === "completed";
                const risk = f.status === "at_risk";
                return (
                  <div
                    key={f.day}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border"
                    data-testid={`followup-${f.day}`}
                  >
                    {done ? (
                      <CheckCircle2 className="h-5 w-5 text-[#2f9e5f]" />
                    ) : risk ? (
                      <AlertTriangle className="h-5 w-5 text-[#c65a63]" />
                    ) : (
                      <Clock className="h-5 w-5 text-muted-foreground" />
                    )}
                    <div className="flex-1">
                      <div className="text-sm font-medium">Day {f.day}</div>
                      <div className="text-xs text-muted-foreground">
                        Due {fmtDate(f.due_at)}
                        {f.note ? ` · ${f.note}` : ""}
                      </div>
                    </div>
                    {!done && canManage && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => completeFollowup(f.day)}
                        data-testid={`followup-complete-${f.day}`}
                      >
                        Complete
                      </Button>
                    )}
                    {done && <StatusBadge value="completed" />}
                  </div>
                );
              })}
              {!(placement.follow_ups || []).length && (
                <div className="text-sm text-muted-foreground">
                  No milestones scheduled.
                </div>
              )}
            </div>
          </div>

          {canManage && (
            <div className="rounded-lg border border-[#255058]/30 bg-[#255058]/5 p-4 space-y-3">
              <div className="flex items-center gap-2 font-display font-semibold text-sm">
                <ShieldAlert className="h-4 w-4 text-[#255058]" /> Warranty
                Management
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <label className="text-xs">Warranty Risk</label>
                  <Select value={risk} onValueChange={setRisk}>
                    <SelectTrigger data-testid="warranty-risk-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs">Employment Status</label>
                  <Select value={empStatus} onValueChange={setEmpStatus}>
                    <SelectTrigger data-testid="warranty-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="resigned">Resigned</SelectItem>
                      <SelectItem value="terminated">Terminated</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Textarea
                rows={2}
                placeholder="Note (recorded in audit trail)…"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <Button
                className="w-full bg-[#255058] hover:bg-[#1c3d43] text-white"
                onClick={saveWarranty}
                data-testid="warranty-save"
              >
                Update Warranty
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Info({ k, v }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{k}</div>
      <div className="font-medium">{v}</div>
    </div>
  );
}
